import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useCommunity } from "contexts/community";
import { useAuth } from "@contexts/AuthContext";
import { useScreen } from "Screen/ScreenContext";

import api from "@helpers/api";

// Query param deep-linking to one group of the community (`/:slug?group=<id>`),
// stamped by the post composer after publishing. Consumed once, then dropped.
const GROUP_PARAM = "group";

export default function useGroup({ community, triggerEdit }) {
    const { t } = useTranslation("community");
    const { loadCommunities } = useCommunity();
    const { user } = useAuth();
    const { bodyRef } = useScreen();

    const [searchParams, setSearchParams] = useSearchParams();
    const requestedGroup = searchParams.get(GROUP_PARAM);

    const createPostModalRef = useRef();
    const tabsAnchorRef = useRef(null);
    const groupContainerRef = useRef(null);

    const findGroup = (id) => community.groups?.find((g) => g.id === id);

    const [group, setGroup] = useState(
        () =>
            (requestedGroup && findGroup(requestedGroup)?.id) ||
            (community.groups?.length ? community.groups[0].id : null)
    );
    const [groupInfo, setGroupInfo] = useState(null);
    const [loadingGroupInfo, setLoadingGroupInfo] = useState(false);
    const [feedRefreshKey, setFeedRefreshKey] = useState(0);

    useEffect(() => {
        if (group) {
            loadGroupInfo();
        } else {
            setGroupInfo(null);
        }
    }, [group]);

    // `?group=<id>` arriving while mounted (published from the sidebar while
    // already on this community) switches the tab; on a fresh mount the state
    // initializer already picked it up. Either way the param is then removed.
    useEffect(() => {
        if (!requestedGroup) return;

        if (requestedGroup !== group && findGroup(requestedGroup)) {
            handleChangeGroup(requestedGroup);
        }

        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(GROUP_PARAM);
                return next;
            },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedGroup]);

    function scrollTabsToTop() {
        const body = bodyRef?.current;
        const anchor = tabsAnchorRef.current;
        if (!body || !anchor) return;
        const bodyRect = body.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();
        const target = body.scrollTop + (anchorRect.top - bodyRect.top);
        body.scrollTo({ top: target, behavior: "smooth" });
    }

    function handleChangeGroup(newGroupId) {
        setGroup(newGroupId);
        scrollTabsToTop();
    }

    async function loadGroupInfo() {
        if (!group) return;
        setGroupInfo(null);
        setLoadingGroupInfo(true);
        try {
            const { data } = await api.get("community/group/" + group);
            setGroupInfo(data);
        } catch (e) {
            console.log(e);
            setGroupInfo(null);
        }
        setLoadingGroupInfo(false);
    }

    function handleCreatePost() {
        createPostModalRef.current.open({ group, community }, (result) => {
            if (result?.gamification) {
                const { points } = result.gamification;
                if (points.post || points.activity) {
                    window.setSnackbar(
                        t("group.pointsEarned", {
                            value: points.post + points.activity,
                        })
                    );
                }
            }
            setFeedRefreshKey((k) => k + 1);
            loadCommunities();
        });
    }

    const currentGroupData = community.groups?.find((g) => g.id === group);

    // Access is resolved server-side per requesting user (role + active plan)
    // and reported as `availability.available`; only an explicit false locks.
    const hasAccess =
        community.isAdmin() ||
        currentGroupData?.availability?.available !== false;
    const groupAvailability = currentGroupData?.availability ?? null;

    return {
        community,
        user,
        group,
        groupInfo,
        loadingGroupInfo,
        hasAccess,
        groupAvailability,
        isAdmin: community.isAdmin(),
        tabsAnchorRef,
        groupContainerRef,
        createPostModalRef,
        feedRefreshKey,
        handleChangeGroup,
        handleCreatePost,
        triggerEdit,
    };
}
